/**
 * Block-side "Question" section shared by the four question block editors.
 *
 * Unselected blocks get the QuestionCombobox (search existing / create new);
 * selected blocks show a Change affordance. Changing shows the combobox again
 * without touching the block until a replacement is picked — selecting calls
 * onChange(dtoToBlockFields) to materialize the referenced question's fields.
 */
import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { PublishJobSummary, QuestionDefinitionDto } from '@course-authoring/shared-types';
import { useTheme } from '@course-authoring/shared-types';

import { formatQuestionHumanId } from '../../utils/humanIds';
import { QuestionCombobox } from './QuestionCombobox';
import { CreateQuestionModal } from './CreateQuestionModal';
import { LessonQuestionPreparationStatus } from './LessonQuestionPreparationStatus';
import { seedLessonQuestionPreparation } from './useLessonQuestionPreparation';
import { dtoToBlockFields, type QuestionBlock } from './questionBlockConversion';

export interface QuestionSelectorProps {
  block: QuestionBlock;
  onChange: (updates: Record<string, unknown>) => void;
}

export function QuestionSelector({ block, onChange }: QuestionSelectorProps): React.ReactElement {
  const { tokens } = useTheme();
  const queryClient = useQueryClient();
  const [changing, setChanging] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createSearchText, setCreateSearchText] = useState('');

  const hasSelection = block.questionDefinitionId !== undefined;
  const showCombobox = !hasSelection || changing;
  const gated = block.type === 'single-choice-answer' ? block.gated : false;

  const styles = useMemo((): Record<string, CSSProperties> => ({
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
      marginTop: tokens.spacing.sm,
      marginBottom: tokens.spacing.sm,
    },
    labelRow: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'baseline',
      gap: tokens.spacing.sm,
    },
    label: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: '12px',
      fontWeight: 700,
      color: tokens.text.muted,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    linkButton: {
      background: 'none',
      border: 'none',
      color: tokens.brand.accent ?? tokens.brand.primary,
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
      fontSize: '13px',
      fontWeight: 500,
      padding: 0,
    },
  }), [tokens]);

  const applySelection = (dto: QuestionDefinitionDto) => {
    onChange(dtoToBlockFields(dto));
    setChanging(false);
  };

  const applyCreatedSelection = (dto: QuestionDefinitionDto & { publishJob?: PublishJobSummary | null }) => {
    if (dto.publishJob !== undefined) {
      seedLessonQuestionPreparation(queryClient, dto.questionDefinitionId, dto.publishJob);
    }
    applySelection(dto);
  };

  const handleCreateNew = (searchText: string) => {
    setCreateSearchText(searchText);
    setCreateModalOpen(true);
  };

  return (
    <div style={styles.container}>
      <div style={styles.labelRow}>
        <span style={styles.label}>
          {block.questionDefinitionId !== undefined
            ? `Question · ${formatQuestionHumanId(block.questionDefinitionId)}`
            : 'Question'}
        </span>
        {hasSelection && !changing && (
          <button
            type="button"
            style={styles.linkButton}
            onClick={() => setChanging(true)}
            aria-label="Change question"
          >
            Change
          </button>
        )}
        {hasSelection && block.questionDefinitionId !== undefined && (
          <LessonQuestionPreparationStatus questionId={block.questionDefinitionId} />
        )}
        {hasSelection && changing && (
          <button
            type="button"
            style={styles.linkButton}
            onClick={() => setChanging(false)}
            aria-label="Cancel changing question"
          >
            Cancel
          </button>
        )}
      </div>

      {showCombobox && (
        <QuestionCombobox
          blockType={block.type}
          onSelect={applySelection}
          onCreateNew={handleCreateNew}
        />
      )}

      <CreateQuestionModal
        blockType={block.type}
        gated={gated}
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={applyCreatedSelection}
        initialQuestionText={createSearchText}
        initialTitle={block.title}
      />
    </div>
  );
}

QuestionSelector.displayName = 'QuestionSelector';
